import React, { useEffect, useState } from 'react';
import { CONFIG } from '../../../../config/mainConfig';
import { stressTestBooksGeneration } from '../services/Settings.service';
import { BookImageGenerationMode, BookModel } from '../../../../interfaces/book.interface';
import { AdminBooksTab } from '../../../../components/Admin';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { getAdminUserBooks } from '../../../../services/AdminUser.service';
import LoadingSpinner from '../../../../components/LoadingSpinner/LoadingSpinner';
import Loader from '../../../../components/Common/Loader';
import { useFormik } from 'formik';
import * as Yup from 'yup';


interface StressTestingProps {
}

export const getValidationFormSchema = () => {
  return Yup.object().shape({
    books: Yup.number().required('Books count is required'),
    pagesCount: Yup.number().required('Pages Count count is required'),
    generationMode: Yup.string().required('Generation Mode is required')
  });
};

const StressTesting: React.FC<StressTestingProps> = ({}: StressTestingProps) => {
  const [pageOptions, setPageOptions] = useState<any[]>([]);
  const [maxPages, setMaxpages] = useState<number>(32);
  const [bookPages, setBookPages] = useState<string[]>([]);

  const [totalBooksPages, setTotalBooksPages] = useState<number | null>(null);
  const [booksPageNumber, setBooksPageNumber] = useState<number>(1);
  const [books, setBooks] = useState<BookModel[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [generateLoader, setGenerateLoader] = useState<boolean>(false);
  const user = useSelector((state: RootState) => state.user.user);


  const initialFormData = {
    books: CONFIG.ADMIN.DEFAULT_STRESS_TESTING_BOOKS,
    generationMode: BookImageGenerationMode.fast,
    pagesCount: 32
  };

  const formik = useFormik({
    initialValues: initialFormData,
    validationSchema: getValidationFormSchema(),
    enableReinitialize: true,
    onSubmit: (values) => {
      handleStartStressTesting();
    }
  });

  const handleStartStressTesting = async () => {
    try {
      setGenerateLoader(true);
      await stressTestBooksGeneration(formik.values.books, formik.values.pagesCount, formik.values.generationMode);
      handleFetchUserBooks();
    } finally {
      setGenerateLoader(false);
    }
  };

  const handleFetchUserBooks = async (getAllBooks?: boolean) => {
    if (!user) {
      return;
    }
    try {
      setLoader(true);
      const { books, totalBooksPages } = await getAdminUserBooks(user.email!, booksPageNumber, getAllBooks);
      setBooks(books);
      setTotalBooksPages(totalBooksPages);
    } catch (error) {
      console.error('Error fetching user books', error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    handleFetchUserBooks();
    setPageOptions([]);
    let newOptions: any = [];
    for (let i = 16; i <= maxPages; i = i + 4) {
      newOptions.push(
        <option key={i} value={i}>{i}</option>
      );
    }
    setPageOptions(newOptions);
    const filteredRecords = newOptions.filter(
      (record: {
        key: string
      }) => parseInt(record.key) <= CONFIG.MAX_LIMIT_PAGES_TO_CREATE);

    setBookPages(filteredRecords);
  }, []);

  return (
    <div className="flex flex-col">
      <h2 className="text-xl font-semibold">Stress Testing</h2>
      <form
        id="form-admin-plans"
        name="form-admin-plans"
        className="text-start bg-storytan mt-2"
        onSubmit={formik.handleSubmit}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-x-2 md:gap-y-[.25rem]">
          <div className="flex flex-col gap-2">
            <label className="font-medium">Create Books</label>
            <input
              type="number"
              id="books"
              name="books"
              value={formik.values.books}
              onChange={formik.handleChange}
              min={1}
              max={100}
              placeholder="Number of books"
              className={`p-2 w-16 rounded-md disabled:bg-gray-200 disabled:text-gray-500 bg-white`}
            />
            <div className="text-red-600 mt-1">{formik.errors.books}</div>
          </div>
          <div className="flex flex-col gap-2">
            <label className="font-medium">Generation mode</label>
            <select
              name="generationMode"
              value={formik.values.generationMode}
              onChange={formik.handleChange}
              className="p-2 w-full rounded-md"
            >
              <option value="">Select Mode</option>
              <option value={BookImageGenerationMode.fast}>Fast</option>
              <option value={BookImageGenerationMode.relaxed}>Relax</option>
              <option value={BookImageGenerationMode.mixed}>Mixed (50% each one)</option>
            </select>
            <div className="text-red-600 mt-1">{formik.errors.generationMode}</div>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-left block text-sm font-medium leading-6 text-gray-900">
              Page Total:
            </label>
            <select
              name="pagesCount"
              value={formik.values.pagesCount}
              onChange={formik.handleChange}
              className="p-2 w-full rounded-md"
            >
              <option value="" disabled>
                Please Select Page Total
              </option>
              {bookPages}
            </select>
            <div className="text-red-600 mt-1">{formik.errors.pagesCount}</div>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <button
            type={'submit'}
            className="flex ml-2 px-3 py-1 bg-storyblue text-white rounded-lg hover:bg-blue-600 transition"
          >
            Start
            <Loader loader={generateLoader} dimentions={'w-6 h-6'} />
          </button>
          <button
            type={'button'}
            className="ml-2 px-3 py-1 bg-storyblue text-white rounded-lg hover:bg-blue-600 transition"
            onClick={() => handleFetchUserBooks()}
          >
            Refresh
          </button>
          <button
            type={'button'}
            className="ml-2 px-3 py-1 bg-storyblue text-white rounded-lg hover:bg-blue-600 transition"
            onClick={() => handleFetchUserBooks(true)}
          >
            Fetch all Books
          </button>
        </div>
      </form>

      <div className="flex justify-center w-full mt-6">
        {loader ? (
            <LoadingSpinner />
          ) :
          (<AdminBooksTab books={books}
                          totalBooksPages={totalBooksPages ?? 0}
                          currentPage={booksPageNumber}
                          handleBooksPageChange={(page) => setBooksPageNumber(+page)} />)}
      </div>
    </div>
  );
};

export default StressTesting;
